import { apiClient } from './authService';

const factureService = {
  // Get all factures
  getAllFactures: async (filters = {}) => {
    try {
      // Build query parameters for filters
      const queryParams = new URLSearchParams();
      
      if (filters.supplier_id) {
        queryParams.append('supplier_id', filters.supplier_id);
      }
      
      if (filters.start_date && filters.end_date) {
        queryParams.append('start_date', filters.start_date);
        queryParams.append('end_date', filters.end_date);
      }
      
      if (filters.unpaid) {
        queryParams.append('unpaid', 'true');
      }

      // Load the full set (server default is only 50 rows). Callers reduce over
      // this list for the « Total / Payé / Non payé » cards, so a truncated page
      // would undercount. Mirrors customerService.getAllCustomers(page_size=5000).
      queryParams.append('page_size', filters.page_size || 5000);

      const url = `factures/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get(url);
      
      // Handle both array responses and object responses with results property
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      console.error('Error fetching factures:', error);
      throw error.response?.data || { message: 'Error fetching factures' };
    }
  },

  // Get facture by ID
  getFactureById: async (id) => {
    try {
      const response = await apiClient.get(`factures/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching facture ${id}:`, error);
      throw error.response?.data || { message: 'Error fetching facture details' };
    }
  },

  // Create new facture
  createFacture: async (factureData) => {
    try {
      // Log the request data for debugging
      console.log('Sending facture data:', JSON.stringify(factureData));
      const response = await apiClient.post('factures/', factureData);
      return response.data;
    } catch (error) {
      console.error('Error creating facture:', error);
      throw error.response?.data || { message: 'Error creating facture' };
    }
  },

  // Update existing facture
  updateFacture: async (id, factureData) => {
    try {
      console.log('Updating facture data:', JSON.stringify(factureData));
      const response = await apiClient.put(`factures/${id}/`, factureData);
      return response.data;
    } catch (error) {
      console.error('Error updating facture:', error);
      throw error.response?.data || { message: 'Error updating facture' };
    }
  },

  // Delete facture
  deleteFacture: async (id) => {
    try {
      const response = await apiClient.delete(`factures/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting facture:', error);
      throw error.response?.data || { message: 'Error deleting facture' };
    }
  },

  // Get unpaid factures
  getUnpaidFactures: async () => {
    try {
      const response = await apiClient.get('factures/unpaid/');
      return response.data;
    } catch (error) {
      console.error('Error fetching unpaid factures:', error);
      throw error.response?.data || { message: 'Error fetching unpaid factures' };
    }
  },

  // Attach a photo of the paper invoice (upload or camera capture).
  // The apiClient defaults to JSON, so the multipart header must be forced
  // (same fix as clientRecordService.saveRecord).
  uploadFactureImage: async (factureId, file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await apiClient.post(`factures/${factureId}/image/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading image for facture ${factureId}:`, error);
      throw error.response?.data || { message: "Erreur lors de l'envoi de la photo" };
    }
  },

  // Remove the attached invoice photo
  deleteFactureImage: async (factureId) => {
    try {
      const response = await apiClient.delete(`factures/${factureId}/image/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting image for facture ${factureId}:`, error);
      throw error.response?.data || { message: 'Erreur lors de la suppression de la photo' };
    }
  },

  // Update facture payment
  updateFacturePayment: async (factureId, paymentData) => {
    try {
      // Use the actual facture ID, not the barcode
      const response = await apiClient.post(`factures/${factureId}/payment/`, paymentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating facture ${factureId} payment:`, error);
      throw error.response?.data || { message: 'Error updating facture payment' };
    }
  },
  
  // Generate printable receipt for a facture
  generatePDF: async (factureId) => {
    try {
      // Use regular response type since we're expecting HTML
      const response = await apiClient.get(`factures/${factureId}/generate_pdf/`);
      
      // Open the HTML in a new window/tab
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        // Write the HTML content to the new window
        printWindow.document.write(response.data);
        printWindow.document.close();
        
        // Add a print script that will trigger print dialog once content loads
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500); // Short delay to ensure content is rendered
        };
        
        return 'success';
      } else {
        throw new Error('Le navigateur a bloqué l\'ouverture d\'une nouvelle fenêtre. Veuillez autoriser les pop-up pour ce site.');
      }
    } catch (error) {
      console.error(`Error generating receipt for facture ${factureId}:`, error);
      throw error.response?.data || { message: error.message || 'Erreur lors de la génération du reçu' };
    }
  }
};

export default factureService;
